import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import useFileView, { FileViewRow, QueueVariant } from "./useFileView";

const SectionCard = styled(Box)({
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  padding: padding.medium1,
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.medium1,
  marginBottom: gaps.small1,
  paddingBottom: gaps.xsmall2,
  borderBottom: `1px solid ${colors.grey09}`,
});

const SectionTitle = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const HeaderCell = styled(TableCell)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  padding: `${padding.xsmall2} ${padding.small1}`,
});

const BodyCell = styled(TableCell)({
  fontSize: fontSize.small,
  color: colors.black,
  padding: `${padding.xsmall2} ${padding.small1}`,
});

const PolicyLink = styled("span")({
  color: colors.blue01,
  textDecoration: "underline",
  cursor: "pointer",
});

const TransCodeChip = styled("span")({
  display: "inline-block",
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold1,
  padding: `1px ${padding.xsmall1}`,
  borderRadius: borderRadius.xsmall1,
  border: `1px solid ${colors.grey09}`,
  backgroundColor: colors.grey10,
  color: colors.black,
});

const QUEUE_STYLES: Record<
  QueueVariant,
  { bg: string; color: string; border: string; label: string }
> = {
  history: { bg: "#e5e7eb", color: "#374151", border: "#9ca3af", label: "History" },
  close: { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd", label: "Close" },
  qr: { bg: "#fce8e8", color: "#c62828", border: "#fca5a5", label: "Quality Review" },
  preclose: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d", label: "Pre-Close" },
  "preclose-hold": {
    bg: "#ffedd5",
    color: "#9a3412",
    border: "#fdba74",
    label: "Pre-Close - Hold",
  },
};

const QueueChip = ({ variant }: { variant: QueueVariant }) => {
  const s = QUEUE_STYLES[variant];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontSize: fontSize.xsmall,
        fontWeight: fontWeight.bold1,
        padding: `1px ${padding.xsmall1}`,
        borderRadius: borderRadius.xsmall1,
        border: `1px solid ${s.border}`,
        backgroundColor: s.bg,
        color: s.color,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </Box>
  );
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

// Always render an amount (even $0.00) — used for premium/retention/total-due.
const money = (v: number | null | undefined): string =>
  v == null ? "" : currencyFormatter.format(v);

// Blank when there's no meaningful value (null or 0) — used for liability /
// reissue columns, which the mockup leaves empty on endorsement line items.
const moneyNonZero = (v: number | null | undefined): string =>
  v == null || v === 0 ? "" : currencyFormatter.format(v);

type FileViewSectionProps = {
  // The record backing the Product Details page this section is rendered in.
  // Used to open a Product Details page for the clicked line-item policy.
  record?: PolicyManagementRecord;
};

const FileViewSection = ({ record }: FileViewSectionProps) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  // Line items are all products/transactions sharing this record's file number
  // + agent. Sourced from the backend (GetFileByNumberAgent).
  const { rows, totals, isLoading } = useFileView(
    record?.FileNumber,
    record?.AgentID
  );

  // Clicking a policy number opens the Product Details page for that policy.
  // File View rows are line items of the current file, so we reuse the parent
  // record and override the policy number/product for the destination header.
  // In-app navigation keeps the MSAL session intact (no re-login).
  const handlePolicyClick = (row: FileViewRow) => {
    if (!record) return;
    navigate(`/policyManagement/product-details/${record.uniqueIdentifier}`, {
      state: {
        // The policy we're navigating away from, so the destination page can
        // offer a labelled "Back" affordance to return here.
        fromPolicyNumber: record.PolicyNumber,
        record: {
          ...record,
          PolicyNumber: row.policyNumber,
          PolicyFormName: row.product || record.PolicyFormName,
        },
      },
    });
  };

  return (
    <SectionCard>
      <SectionHeader
        sx={{
          cursor: "pointer",
          borderBottom: open ? `1px solid ${colors.grey09}` : "none",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <SectionTitle>File View</SectionTitle>
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          {open ? (
            <ExpandLessIcon sx={{ color: colors.blue15 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: colors.blue15 }} />
          )}
        </Box>
      </SectionHeader>
      <Collapse in={open} unmountOnExit>
      {isLoading && rows.length === 0 ? (
        <Typography
          sx={{
            fontSize: fontSize.small,
            color: colors.grey11,
            padding: `${padding.small1} 0`,
          }}
        >
          Loading file view…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography
          sx={{
            fontSize: fontSize.small,
            color: colors.grey11,
            padding: `${padding.small1} 0`,
          }}
        >
          No products found for this file.
        </Typography>
      ) : (
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeaderCell>Product</HeaderCell>
              <HeaderCell>Policy Number</HeaderCell>
              <HeaderCell align="right">Liability Amount</HeaderCell>
              <HeaderCell>Rate Type</HeaderCell>
              <HeaderCell>Reissue</HeaderCell>
              <HeaderCell>Trans Code</HeaderCell>
              <HeaderCell align="right">Premium/Fee</HeaderCell>
              <HeaderCell align="right">Agent Retention</HeaderCell>
              <HeaderCell align="right">Total Due</HeaderCell>
              <HeaderCell>Queue</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={`${r.policyNumber}-${i}`}>
                <BodyCell title={r.product}>{r.product}</BodyCell>
                <BodyCell>
                  <PolicyLink
                    role="link"
                    tabIndex={0}
                    onClick={() => handlePolicyClick(r)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePolicyClick(r);
                      }
                    }}
                  >
                    {r.policyNumber}
                  </PolicyLink>
                </BodyCell>
                <BodyCell align="right">
                  {moneyNonZero(r.liabilityAmount)}
                </BodyCell>
                <BodyCell>{r.rateType}</BodyCell>
                <BodyCell>{moneyNonZero(r.reissue)}</BodyCell>
                <BodyCell>
                  {r.transCode && <TransCodeChip>{r.transCode}</TransCodeChip>}
                </BodyCell>
                <BodyCell align="right">{money(r.premiumFee)}</BodyCell>
                <BodyCell align="right">{money(r.agentRetention)}</BodyCell>
                <BodyCell align="right">{money(r.totalDue)}</BodyCell>
                <BodyCell>
                  <QueueChip variant={r.queue} />
                </BodyCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: colors.grey10 }}>
              <BodyCell sx={{ fontWeight: fontWeight.bold2 }}>TOTALS</BodyCell>
              <BodyCell />
              <BodyCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {moneyNonZero(totals.liabilityAmount)}
              </BodyCell>
              <BodyCell />
              <BodyCell />
              <BodyCell />
              <BodyCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {money(totals.premiumFee)}
              </BodyCell>
              <BodyCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {money(totals.agentRetention)}
              </BodyCell>
              <BodyCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {money(totals.totalDue)}
              </BodyCell>
              <BodyCell />
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      )}
      </Collapse>
    </SectionCard>
  );
};

export default FileViewSection;
